import _ from 'underscore';
import $ from 'jquery';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { withPermissions } from '@bingads-webui-react/hoc-with-permissions';
import { withPreference } from '@bingads-webui-react/preference-context';
import { withI18nConsumer } from '@bingads-webui-react/i18n-context';
import { isSDK } from '@bingads-webui-campaign-react/sdk-utils';
import { getDisplayMode, DisplayMode } from '@bingads-webui-campaign-react/display-mode';
import { keyVault, withPopupContext } from '@bingads-webui-react/popup-manager';
import { withServerContext } from '@bingads-webui-campaign-react/server-context';
import { HotKeyDialogWithI18n, HotKeyNavigator } from '@bingads-webui-react/hot-keys';
import { CampaignHistoryRecorder } from '@bingads-webui-campaign-react/campaign-entity-search';
import { URLFilter } from '@bingads-webui-campaign/url-filter';
import { LightBoxDialog } from '@bingads-webui-campaign-react/lightbox';
import { SmartCampaignsPopup } from '@bingads-webui-campaign-react/smart-campaigns-popup';
import { SupportModals } from '@bingads-webui-react/support-modals';
import { WrappedFloatingBannerContainer } from '@bingads-webui-campaign/notification-center';
import { CalloutView } from '@bingads-webui-campaign-react/callout-service';
import { renderApexHelpSuggestions, renderSearchInputIcon } from '@bingads-webui-campaign-react/apex-help-dropdown-customized';
import { withInstrumentation } from '@bingads-webui-react/hoc-with-instrumentation';
import { isCopilotRegionRestricted } from '@bingads-webui-react/copilot-utils';

import { ApexUpdater } from './apex-updater';
import { WalkMeWithScopeAndContext } from './walk-me';
import { NewUIPreferenceDialog } from './newui-preference-dialog/newui-preference-dialog';
import { RouteChangeLogger } from './route-change-logger';
import { ImportStatusChecker } from './import-status-checker';
import { withCcJsDataContextProvider } from '../contexts/with-cc-js-data-context-provider';
import { PageTitleUpdater } from './page-title-updater';
import { NavigationRefreshLogger } from './navigation-refresh-logger';
import { NavigationRefreshDialog } from './navigation-refresh-dialog/navigation-refresh-dialog';
import { ConsentModeDialog } from './consent-mode-dialog';

const apexHelpContentKey = _.uniqueId(`${keyVault.apexHelpContent}-`);
const helpPopupKey = _.uniqueId(`${keyVault.helpPopup}-`);

const onHelpRailViewOpen = (popupManager) => {
  popupManager.forceAcquireLock(apexHelpContentKey);
};

const onHelpRailViewClose = (popupManager, opener) => {
  popupManager.releaseLock(apexHelpContentKey);
  const $opener = $(opener);
  if (!$opener.is(':visible')) {
    const globalMenuHelpButton = $('.bingads-campaign-react-global-menu #help');
    globalMenuHelpButton.trigger('focus');
  }
};

// A patch to fix a11y bug
const replacePopupTitleTagWithH2 = (rootEle) => {
  const $titleEle = $(rootEle).find('h1#apex_h1_tag');
  if ($titleEle.length > 0) {
    const newTitleHtml = $titleEle.prop('outerHTML').replace('h1', 'h2');
    const $newTitle = $(newTitleHtml).css({
      color: '#323130',
      fontSize: '20px',
      marginBottom: '10px',
    });
    $titleEle.replaceWith($newTitle);
  }
};

const onHelpPopupOpen = (popupManager, popupRootEle) => {
  replacePopupTitleTagWithH2(popupRootEle);
  popupManager.forceAcquireLock(helpPopupKey);
};

const onHelpPopupClose = (popupManager) => {
  popupManager.releaseLock(helpPopupKey);
};

const AfterPageReadyInternal = React.memo((props) => {
  useEffect(() => {
    // this is a temporary logic for the teaching bubble flight
    // we suspect the impression drop of the teaching bubble is caused by the API latency
    // the P95 of the import popup API call is 3 seconds
    // so we delay the priority comparison with 3.5 seconds for 10% of users to run the experiment
    setTimeout(() => {
      // clear gate when page is ready
      props.popupManager.clearGate();
    }, 3500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [isAdsCopilotRegionRestricted, setIsAdsCopilotRegionRestricted] = useState();
  const isAdsCopilotFraudRestrictionEnabled = _.get(props.permissions, ['dynamic', 'AdsCopilotFraudRestriction'], false);
  const isConsentModeEnformentEnabled = _.get(props.permissions, ['dynamic', 'ConsentModeEnforcement'], false);

  useEffect(() => {
    isCopilotRegionRestricted({
      feature: 'Chat',
      permissions: props.permissions,
      serverContext: props.serverContext,
    }).then(setIsAdsCopilotRegionRestricted);
  }, [props.permissions, props.serverContext]);

  const helpSearchBoxCopilotScenario = props.instrumentation.helpSearchBoxCopilotScenario.create();
  const logger = helpSearchBoxCopilotScenario.searchActivity.create();
  const isExpertMode = !_.get(props.serverContext, ['isSmartMode']) && !_.get(props.serverContext, ['isUpSmartMode']);
  const isNewLogo = _.get(props, ['permissions', 'dynamic', 'AdsCopilotUxImprovements']);

  const renderApexHelpSuggestionsWithi18n = useCallback((suggestionData, suggestionContainer, renderSuggestionToHtml, hideSuggestionBox) =>
    renderApexHelpSuggestions(suggestionData, suggestionContainer, renderSuggestionToHtml, hideSuggestionBox, props.i18n, logger, isNewLogo), [props.i18n, logger, isNewLogo]);

  const renderSearchInputIconWithi18n = useCallback(container => renderSearchInputIcon(container, props.i18n, isNewLogo), [props.i18n, isNewLogo]);
  const displayMode = getDisplayMode();

  const supportModalsProps = useMemo(() => {
    if (isAdsCopilotRegionRestricted === null || isAdsCopilotRegionRestricted === undefined) {
      // do not initialize apex until copilot region restriction check has finished
      return undefined;
    }

    // MAE needs to hide copilot button and live chat
    let isMAE = false;
    if (location) {
      const urlParams = new URLSearchParams(location.search);
      isMAE = urlParams.get('applicationName') === 'BingAdsEditor';
    }

    const { SupportOptions } = props.serverContext;
    if (isMAE && SupportOptions) {
      SupportOptions.IfShowLiveChat = false;
    }

    const ret = {
      userContext: _.pick(props.serverContext, ['CurrentAccount', 'CurrentCustomer', 'CurrentUser']),
      supportOptions: SupportOptions,
      permissions: _.extend({}, props.serverContext.Permissions, props.serverContext.DynamicPermissions),
      helpModel: props.serverContext.HelpModel,
      currentLocale: props.serverContext.CurrentLocale,
      i18n: props.i18n,
      resourcePath: props.serverContext.Urls && props.serverContext.Urls.VirtualAgentUrl,

      // apex help container
      onRailViewOpen: _.partial(onHelpRailViewOpen, props.popupManager),
      onRailViewClose: _.partial(onHelpRailViewClose, props.popupManager),

      // help popup
      onPopupOpen: _.partial(onHelpPopupOpen, props.popupManager),
      onPopupClose: _.partial(onHelpPopupClose, props.popupManager),
    };

    const isSDKMode = isSDK();

    if (isSDKMode) {
      _.extend(ret, {
        flowId: _.get(props.serverContext.SupportOptions, ['customSupportOptions', 'flowId']) || 'EXP-main-campaign',
        renderApexHelpSuggestions: isAdsCopilotRegionRestricted ? undefined : renderApexHelpSuggestionsWithi18n,
      });
    } else {
      const isShellless = displayMode === DisplayMode.Shellless;
      _.extend(ret, {
        flowId: 'EXP-main-campaign',
        renderApexHelpSuggestions: (isShellless || isAdsCopilotRegionRestricted || isMAE) ? undefined : renderApexHelpSuggestionsWithi18n,
        renderSearchInputIcon: !isShellless && !isAdsCopilotRegionRestricted && !isMAE ? renderSearchInputIconWithi18n : _.noop,
        isCopilotEnabled: !isShellless && isExpertMode && !isMAE && (!isAdsCopilotRegionRestricted || isAdsCopilotFraudRestrictionEnabled),
      });
    }

    return ret;
  }, [
    isAdsCopilotFraudRestrictionEnabled,
    isAdsCopilotRegionRestricted,
    isExpertMode,
    displayMode,
    props.i18n,
    props.popupManager,
    props.serverContext,
    renderApexHelpSuggestionsWithi18n,
    renderSearchInputIconWithi18n,
  ]);

  return isSDK() ? (
    <React.Fragment>
      <CampaignHistoryRecorder />
      <ApexUpdater />
      <URLFilter />
      <WrappedFloatingBannerContainer />
      <RouteChangeLogger />
      {supportModalsProps && <SupportModals {...supportModalsProps} />}
      <NavigationRefreshLogger />
      <PageTitleUpdater {...props} />
    </React.Fragment>
  ) : (
    <React.Fragment>
      <HotKeyDialogWithI18n />
      <HotKeyNavigator />
      <CampaignHistoryRecorder />
      <ApexUpdater />
      <URLFilter />
      <WalkMeWithScopeAndContext {...props.serverContext} />
      <NewUIPreferenceDialog />
      {isConsentModeEnformentEnabled && <ConsentModeDialog serverContext={props.serverContext} /> }
      <LightBoxDialog
        serverContext={props.serverContext}
        permissions={props.permissions}
        preferenceService={props.preferenceService}
        i18n={props.i18n}
        customerId={props.serverContext.CurrentCustomer.Id}
        contextUrl={window.location.href}
        userId={props.serverContext.CurrentUser.Id}
        setLoading={_.noop}
        onCancel={_.noop}
      />
      <SmartCampaignsPopup i18n={props.i18n} />
      {supportModalsProps && <SupportModals {...supportModalsProps} />}
      <WrappedFloatingBannerContainer />
      <RouteChangeLogger />
      <NavigationRefreshLogger />
      <PageTitleUpdater {...props} />
      <ImportStatusChecker
        i18n={props.i18n}
        serverContext={props.serverContext}
        preferenceService={props.preferenceService}
      />
      <CalloutView props={props} />
      <NavigationRefreshDialog />
    </React.Fragment>
  );
});

AfterPageReadyInternal.propTypes = {
  serverContext: PropTypes.shape({
    CurrentCustomer: PropTypes.shape({
      Id: PropTypes.number,
    }),
    CurrentUser: PropTypes.shape({
      Id: PropTypes.number,
    }),
    Permissions: PropTypes.shape({}),
    DynamicPermissions: PropTypes.shape({}),
    SupportOptions: PropTypes.shape({
      IfShowLiveChat: PropTypes.bool,
    }),
    HelpModel: PropTypes.shape({}),
    CurrentLocale: PropTypes.string,
    Urls: PropTypes.shape({
      VirtualAgentUrl: PropTypes.string,
    }),
  }).isRequired,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      AdsCopilotFraudRestriction: PropTypes.bool,
    }),
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  preferenceService: PropTypes.shape({}).isRequired,
  popupManager: PropTypes.shape({
    clearGate: PropTypes.func,
  }).isRequired,
  instrumentation: PropTypes.shape({ helpSearchBoxCopilotScenario: PropTypes.shape({ create: PropTypes.func }) }).isRequired,
};

export const AfterPageReady =
  _.compose(
    withPopupContext,
    withCcJsDataContextProvider,
    withServerContext,
    withPreference,
    withI18nConsumer,
    withPermissions,
    withInstrumentation
  )(AfterPageReadyInternal);
